import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp, getHospitalName, calcAge } from '../../context/AppContext';
import { sessionsAPI } from '../../services/api';
import './Dashboard.css';

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

function ScoreBadge({ score }) {
  if (score == null) return <span className="chip chip-cyan">No sessions</span>;
  if (score <= 33)   return <span className="chip chip-green">{score}</span>;
  if (score <= 66)   return <span className="chip chip-amber">{score}</span>;
  return               <span className="chip chip-rose">{score}</span>;
}

function PatientCard({ patient, onSelect }) {
  const initials    = patient.name.split(' ').map((w) => w[0]).slice(0, 2).join('');
  const lastSession = patient.latestSession ?? null;
  return (
    <div className="patient-card" onClick={() => onSelect(patient)}>
      <div className="patient-card-avatar">{initials}</div>
      <div className="patient-card-body">
        <div className="patient-card-top">
          <h3 className="patient-name">{patient.name}</h3>
          <ScoreBadge score={lastSession?.compositeScore ?? null} />
        </div>
        <div className="patient-card-meta">
          <span>{patient.dateOfBirth ? calcAge(patient.dateOfBirth) : patient.age} yrs</span>
          <span className="meta-dot">·</span>
          <span>{patient.gender}</span>
          {patient.prevMedicalCond && (
            <><span className="meta-dot">·</span><span className="meta-cond">{patient.prevMedicalCond}</span></>
          )}
        </div>
        <div className="patient-card-footer">
          <span className="recording-count">
            {patient.sessionCount ?? 0} session{(patient.sessionCount ?? 0) !== 1 ? 's' : ''}
          </span>
          {lastSession && (
            <span className="last-recording">
              Last: {new Date(lastSession.createdAt).toLocaleDateString('en-IN', {
                day: 'numeric', month: 'short', year: 'numeric',
              })}
            </span>
          )}
        </div>
      </div>
      <div className="patient-card-arrow">›</div>
    </div>
  );
}

const GENDERS = ['Male', 'Female', 'Other', 'Prefer not to say'];

// ── Task definitions ──────────────────────────────────────────────────────────
const TASKS = [
  { suffix: 'aa',    label: 'Sustained Vowel (/aa/)',  hint: 'e.g. patient_aa.wav'  },
  { suffix: 'glide', label: 'Pitch Glide',             hint: 'e.g. patient_glide.wav' },
  { suffix: 'mpt',   label: 'Maximum Phonation Time',  hint: 'e.g. patient_mpt.wav'  },
  { suffix: 'text',  label: 'Reading Passage',         hint: 'e.g. patient_text.wav' },
];

// ── Score colour helper (severity: low = healthy, high = impaired) ───────────
function scoreColor(score) {
  if (score == null) return 'var(--text-muted)';
  if (score <= 33)   return 'var(--accent-green)';
  if (score <= 66)   return '#F59E0B';
  return 'var(--accent-rose)';
}
function scoreLabel(score) {
  if (score == null) return '—';
  if (score <= 33)   return 'Normal';
  if (score <= 66)   return 'Mild';
  return 'Severe';
}

// ── Upload Session Modal ──────────────────────────────────────────────────────
function UploadSessionModal({ onClose, patients }) {
  const { actions, currentUser } = useApp();
  const navigate = useNavigate();

  const [files,         setFiles]         = useState({ aa: null, glide: null, mpt: null, text: null });
  const [patientId,     setPatientId]     = useState('');
  const [recordingDate, setRecordingDate] = useState(new Date().toISOString().split('T')[0]);
  const [doctorNote,    setDoctorNote]    = useState('');
  const [errors,        setErrors]        = useState({});
  const [stage,         setStage]         = useState('form');
  const [result,        setResult]        = useState(null);
  const [errMsg,        setErrMsg]        = useState('');
  const [pdfLoading,    setPdfLoading]    = useState(false);

  const fileRefs = { aa: useRef(), glide: useRef(), mpt: useRef(), text: useRef() };

  const validate = () => {
    const e = {};
    if (!patientId)       e.patientId = 'Select a patient';
    if (!recordingDate)   e.recordingDate = 'Select a date';
    TASKS.forEach(({ suffix, label }) => {
      if (!files[suffix]) e[suffix] = `${label} file is required`;
    });
    return e;
  };

  const handleFile = (suffix) => (e) => {
    const file = e.target.files?.[0] || null;
    setFiles((p) => ({ ...p, [suffix]: file }));
    setErrors((p) => ({ ...p, [suffix]: '' }));
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setStage('analysing');

    try {
      const patient = patients.find((p) => p._id === patientId);

      // Build properly named tracks — rename each file to our standard format:
      // {patientID}_{sessionCount+1}_{date}_{suffix}.wav
      // The backend also renames on its side, but we set fileName here so
      // the FormData field names match what multer expects.
      const tracks = TASKS.map(({ suffix }) => ({
        taskSuffix: suffix,
        blob:       files[suffix],
        fileName:   files[suffix].name,  // backend will rename to standard format
      }));

      const { session } = await sessionsAPI.create(patientId, tracks, {
        recordingDate,
        doctorNote: doctorNote.trim() || undefined,
      });

      setResult(session);
      setStage('result');

      // Refresh patient list so session count updates
      actions.fetchPatients().then(({ patients: list }) => {
        // update handled by AppContext via fetchPatients
      }).catch(() => {});

    } catch (err) {
      setErrMsg(err.message || 'Analysis failed. Please check the audio files and try again.');
      setStage('error');
    }
  };

  const selectedPatient = patients.find((p) => p._id === patientId);

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && stage !== 'analysing' && onClose()}>
      <div className="modal-card upload-modal animate-in">

        {/* Header */}
        <div className="modal-header">
          <h3>📂 Upload recording session</h3>
          {stage !== 'analysing' && (
            <button className="modal-close" onClick={onClose}>✕</button>
          )}
        </div>

        {/* ── FORM STAGE ── */}
        {stage === 'form' && (
          <>
            <div className="modal-body">

              {/* Patient selector */}
              <div className={`field-group ${errors.patientId ? 'has-error' : ''}`}>
                <label>Patient <span className="required">*</span></label>
                <select
                  className="field-input"
                  value={patientId}
                  onChange={(e) => { setPatientId(e.target.value); setErrors((p) => ({ ...p, patientId: '' })); }}
                >
                  <option value="">— Select patient —</option>
                  {patients.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.name} · {p.patientID}
                    </option>
                  ))}
                </select>
                {errors.patientId && <span className="field-error">{errors.patientId}</span>}
              </div>

              {/* Recording date */}
              <div className={`field-group ${errors.recordingDate ? 'has-error' : ''}`}>
                <label>Date of recording <span className="required">*</span></label>
                <input
                  className="field-input"
                  type="date"
                  value={recordingDate}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => { setRecordingDate(e.target.value); setErrors((p) => ({ ...p, recordingDate: '' })); }}
                />
                {errors.recordingDate && <span className="field-error">{errors.recordingDate}</span>}
              </div>

              {/* Audio file uploads */}
              <div className="upload-files-grid">
                {TASKS.map(({ suffix, label, hint }) => (
                  <div key={suffix} className={`field-group upload-file-row ${errors[suffix] ? 'has-error' : ''}`}>
                    <label>
                      {label} <span className="required">*</span>
                    </label>
                    <div
                      className={`file-drop-zone ${files[suffix] ? 'has-file' : ''}`}
                      onClick={() => fileRefs[suffix].current?.click()}
                    >
                      <input
                        ref={fileRefs[suffix]}
                        type="file"
                        accept=".wav,audio/wav,audio/wave"
                        style={{ display: 'none' }}
                        onChange={handleFile(suffix)}
                      />
                      {files[suffix] ? (
                        <div className="file-selected">
                          <span className="file-icon">🎵</span>
                          <div className="file-info">
                            <span className="file-name">{files[suffix].name}</span>
                            <span className="file-size">{(files[suffix].size / 1024).toFixed(0)} KB</span>
                          </div>
                          <button
                            className="file-clear"
                            onClick={(e) => { e.stopPropagation(); setFiles((p) => ({ ...p, [suffix]: null })); }}
                          >✕</button>
                        </div>
                      ) : (
                        <div className="file-placeholder">
                          <span className="file-upload-icon">⬆</span>
                          <span className="file-hint">{hint}</span>
                        </div>
                      )}
                    </div>
                    {errors[suffix] && <span className="field-error">{errors[suffix]}</span>}
                  </div>
                ))}
              </div>

              {/* Doctor's note */}
              <div className="field-group">
                <label>
                  Doctor's note
                  <span className="optional"> (optional · dated {recordingDate || 'today'})</span>
                </label>
                <textarea
                  className="field-input"
                  rows={3}
                  placeholder="Clinical observations for this session…"
                  value={doctorNote}
                  onChange={(e) => setDoctorNote(e.target.value)}
                />
              </div>

            </div>
            <div className="modal-footer">
              <button className="btn-ghost" onClick={onClose}>Cancel</button>
              <button className="btn-primary" onClick={handleSubmit}>
                ⬆ Upload &amp; Analyse
              </button>
            </div>
          </>
        )}

        {/* ── ANALYSING STAGE ── */}
        {stage === 'analysing' && (
          <div className="modal-body upload-analysing">
            <div className="analysing-spinner" />
            <p className="analysing-title">Uploading &amp; analysing…</p>
            <p className="analysing-sub">
              Files are being uploaded to the cloud and the acoustic analysis is running.
              This usually takes 10–20 seconds.
            </p>
            {selectedPatient && (
              <p className="analysing-patient">Patient: <strong>{selectedPatient.name}</strong></p>
            )}
          </div>
        )}

        {/* ── RESULT STAGE ── */}
        {stage === 'result' && result?.analysis && (
          <>
            <div className="modal-body upload-result">
              <div className="result-success-badge">✓ Analysis complete</div>

              {/* Composite score */}
              <div className="result-composite">
                <span className="result-composite-label">Composite Severity</span>
                <span
                  className="result-composite-value"
                  style={{ color: scoreColor(result.analysis.composite) }}
                >
                  {result.analysis.composite}
                </span>
                <span className="result-composite-denom">/ 100</span>
                <span
                  className="result-composite-rating"
                  style={{ color: scoreColor(result.analysis.composite) }}
                >
                  {scoreLabel(result.analysis.composite)}
                </span>
              </div>

              {/* Subset scores */}
              <div className="result-subsets">
                {[
                  { label: 'Stability',  value: result.analysis.stability  },
                  { label: 'Clarity',    value: result.analysis.clarity    },
                  { label: 'Efficiency', value: result.analysis.efficiency },
                ].map(({ label, value }) => (
                  <div key={label} className="result-subset-card">
                    <span className="result-subset-label">{label}</span>
                    <span className="result-subset-value" style={{ color: scoreColor(value) }}>
                      {value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Metrics table */}
              <div className="result-metrics">
                <table className="metrics-table">
                  <thead>
                    <tr><th>Metric</th><th>Raw</th><th>Severity</th></tr>
                  </thead>
                  <tbody>
                    {[
                      { label: 'Jitter (%)',         raw: result.analysis.raw.jitter,         score: result.analysis.scores.jitter         },
                      { label: 'Shimmer (%)',        raw: result.analysis.raw.shimmer,        score: result.analysis.scores.shimmer        },
                      { label: 'F0 Variability (%)', raw: result.analysis.raw.f0_variability, score: result.analysis.scores.f0_variability },
                      { label: 'HNR (dB)',           raw: result.analysis.raw.hnr,            score: result.analysis.scores.hnr            },
                      { label: 'CPPS (dB)',          raw: result.analysis.raw.cpps,           score: result.analysis.scores.cpps           },
                      { label: 'MPT (s)',            raw: result.analysis.raw.mpt,            score: result.analysis.scores.mpt            },
                      { label: 'Glide Range (st)',   raw: result.analysis.raw.glide,          score: result.analysis.scores.glide          },
                    ].map(({ label, raw, score }) => (
                      <tr key={label}>
                        <td>{label}</td>
                        <td className="metric-raw">{raw ?? '—'}</td>
                        <td className="metric-score" style={{ color: scoreColor(score) }}>
                          {score ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer upload-result-footer">
              <button className="btn-ghost" onClick={onClose}>Close</button>
              <div className="result-footer-right">
                {result?.analysis && (
                  <button
                    className="btn-ghost"
                    disabled={pdfLoading}
                    onClick={async () => {
                      setPdfLoading(true);
                      try {
                        const { reportUrl } = await sessionsAPI.getReport(result._id);
                        window.open(reportUrl, '_blank');
                      } catch (err) {
                        actions.showToast('Could not open report: ' + err.message, 'error');
                      } finally {
                        setPdfLoading(false);
                      }
                    }}
                  >
                    {pdfLoading ? '⏳…' : '⬇ PDF Report'}
                  </button>
                )}
                <button
                  className="btn-primary"
                  onClick={() => {
                    const patient = patients.find((p) => p._id === patientId);
                    if (patient) {
                      actions.selectPatient(patient);
                      onClose();
                      navigate('/patient');
                    }
                  }}
                >
                  View patient →
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── ERROR STAGE ── */}
        {stage === 'error' && (
          <>
            <div className="modal-body upload-error">
              <div className="error-icon">⚠</div>
              <p className="error-title">Upload failed</p>
              <p className="error-msg">{errMsg}</p>
            </div>
            <div className="modal-footer">
              <button className="btn-ghost" onClick={onClose}>Close</button>
              <button className="btn-primary" onClick={() => { setStage('form'); setErrMsg(''); }}>
                Try again
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
}

function AddPatientModal({ onClose, onSubmit, loading }) {
  const [form,   setForm]   = useState({ name: '', dateOfBirth: '', gender: 'Male', prevMedicalCond: '', initialNote: '' });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!form.name.trim())   errs.name        = 'Name is required';
    if (!form.dateOfBirth)   errs.dateOfBirth = 'Date of birth is required';
    return errs;
  };

  const handleSubmit = () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    const dob = new Date(form.dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    onSubmit({ ...form, age, dateOfBirth: form.dateOfBirth });
  };

  const handleChange = (f) => (e) => {
    setForm((p) => ({ ...p, [f]: e.target.value }));
    setErrors((p) => ({ ...p, [f]: '' }));
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-card animate-in">
        <div className="modal-header">
          <h3>Register new patient</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="modal-row">
            <div className={`field-group ${errors.name ? 'has-error' : ''}`}>
              <label>Full name <span className="required">*</span></label>
              <input className="field-input" placeholder="e.g. Ravi Kumar" value={form.name} onChange={handleChange('name')} />
              {errors.name && <span className="field-error">{errors.name}</span>}
            </div>
            <div className={`field-group ${errors.dateOfBirth ? 'has-error' : ''}`}>
              <label>Date of birth <span className="required">*</span></label>
              <input className="field-input" type="date" value={form.dateOfBirth}
                onChange={handleChange('dateOfBirth')}
                max={new Date().toISOString().split('T')[0]} />
              {errors.dateOfBirth && <span className="field-error">{errors.dateOfBirth}</span>}
            </div>
          </div>
          <div className="field-group">
            <label>Gender</label>
            <select className="field-input" value={form.gender} onChange={handleChange('gender')}>
              {GENDERS.map((g) => <option key={g}>{g}</option>)}
            </select>
          </div>
          <div className="field-group">
            <label>Previous medical conditions</label>
            <textarea className="field-input" rows={3} placeholder="e.g. Hypertension, GERD…"
              value={form.prevMedicalCond} onChange={handleChange('prevMedicalCond')} />
          </div>
          <div className="field-group">
            <label>Initial note <span className="optional">(optional)</span></label>
            <textarea className="field-input" rows={3} placeholder="Initial clinical observation…"
              value={form.initialNote} onChange={handleChange('initialNote')} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
            {loading ? <><span className="spinner-sm" /> Saving…</> : 'Register & open →'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { actions, currentUser } = useApp();
  const navigate = useNavigate();

  const [patients,     setPatients]     = useState([]);
  const [search,       setSearch]       = useState('');
  const [sortBy,       setSortBy]       = useState('name');
  const [showModal,    setShowModal]    = useState(false);
  const [showUpload,   setShowUpload]   = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [pageLoading,  setPageLoading]  = useState(true);
  const [pageError,    setPageError]    = useState(null);

  useEffect(() => {
    setPageLoading(true);
    actions.fetchPatients()
      .then(({ patients: list }) => setPatients(list))
      .catch(() => setPageError('Failed to load patients. Please refresh.'))
      .finally(() => setPageLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q    = search.toLowerCase();
    let   list = patients.filter((p) =>
      p.name.toLowerCase().includes(q) ||
      (p.prevMedicalCond || '').toLowerCase().includes(q)
    );
    if (sortBy === 'name')   list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    if (sortBy === 'age')    list = [...list].sort((a, b) => a.age - b.age);
    if (sortBy === 'recent') list = [...list].sort((a, b) => {
      const ad = a.latestSession?.createdAt || '';
      const bd = b.latestSession?.createdAt || '';
      return bd.localeCompare(ad);
    });
    return list;
  }, [patients, search, sortBy]);

  const handleSelectPatient = (patient) => {
    actions.selectPatient(patient);
    navigate('/patient');
  };

  const handleAddPatient = async (data) => {
    setModalLoading(true);
    try {
      const patient = await actions.addPatient(data);
      setShowModal(false);
      actions.selectPatient(patient);
      navigate('/patient');
    } catch (err) {
      actions.showToast(err.message || 'Failed to register patient', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const totalSessions = patients.reduce((acc, p) => acc + (p.sessionCount ?? 0), 0);

  if (pageError) {
    return (
      <div className="page-wrapper">
        <div className="content-container empty-state">
          <div className="empty-icon">⚠</div>
          <h3>Failed to load</h3>
          <p>{pageError}</p>
          <button className="btn-primary" onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  const hospitalName = getHospitalName(currentUser);

  return (
    <div className="page-wrapper">
      <div className="content-container">

        <div className="dashboard-header animate-in">
          <div>
            <p className="dashboard-greeting">Good {getGreeting()},</p>
            <h1 className="dashboard-title">{currentUser?.name}</h1>
            <p className="dashboard-sub">{currentUser?.specialisation}{hospitalName ? ` · ${hospitalName}` : ''}</p>
          </div>
          <div className="dashboard-header-actions">
            <button className="btn-ghost" onClick={() => setShowUpload(true)}>
              📂 Upload session
            </button>
            <button className="btn-primary add-patient-btn" onClick={() => setShowModal(true)}>
              <span>+</span> New patient
            </button>
          </div>
        </div>

        <div className="stats-row animate-in stagger-1">
          <div className="stat-card">
            <span className="stat-label">Total Patients</span>
            <span className="stat-value">{patients.length}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Voice Sessions</span>
            <span className="stat-value">{totalSessions}</span>
          </div>

        </div>

        <div className="list-controls animate-in stagger-2">
          <div className="search-wrapper">
            <span className="search-icon">⌕</span>
            <input className="search-input" placeholder="Search by name or condition…"
              value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <button className="search-clear" onClick={() => setSearch('')}>✕</button>}
          </div>
          <div className="sort-wrapper">
            <span className="sort-label">Sort:</span>
            <select className="sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="name">Name A–Z</option>
              <option value="age">Age</option>
              <option value="recent">Most recent</option>
            </select>
          </div>
        </div>

        {pageLoading ? (
          <div className="loading-list">
            {[1, 2, 3].map((i) => <div key={i} className="patient-card-skeleton skeleton" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state animate-in">
            <div className="empty-icon">◎</div>
            <h3>{search ? 'No patients found' : 'No patients yet'}</h3>
            <p>{search ? `No results for "${search}"` : 'Register your first patient to get started.'}</p>
            {!search && <button className="btn-primary" onClick={() => setShowModal(true)}>+ New patient</button>}
          </div>
        ) : (
          <div className="patient-list animate-in stagger-3">
            <p className="list-count">{filtered.length} patient{filtered.length !== 1 ? 's' : ''}</p>
            {filtered.map((p, i) => (
              <div key={p._id} style={{ animationDelay: `${i * 0.04}s` }}>
                <PatientCard patient={p} onSelect={handleSelectPatient} />
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <AddPatientModal onClose={() => setShowModal(false)} onSubmit={handleAddPatient} loading={modalLoading} />
      )}

      {showUpload && (
        <UploadSessionModal onClose={() => setShowUpload(false)} patients={patients} />
      )}
    </div>
  );
}
